import { EnvelopeIcon } from '@heroicons/react/20/solid';
import { Tab } from '@headlessui/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from 'shared/stores/authSlice';
import { fetchFavorites } from 'shared/stores/favoritesSlice';
import { fetchSavedSearches } from 'shared/stores/savedSearchesSlice';
import { formatPriceSummary } from 'shared/utils/currency';
import { useTranslation } from 'react-i18next';
import { ROUTES, getRoute } from '../routeConstants';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function DashboardPage() {
    const { t } = useTranslation('common');
    const [activeTab, setActiveTab] = useState('favorites');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, isAuthenticated, loading } = useSelector((state) => state.auth);
    const { favorites, loading: favoritesLoading } = useSelector((state) => state.favorites);
    const { savedSearches, loading: savedSearchesLoading } = useSelector((state) => state.savedSearches);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(ROUTES.LOGIN);
            return;
        }
        
        dispatch(fetchFavorites());
        dispatch(fetchSavedSearches());
    }, [isAuthenticated, dispatch, navigate]);

    const handleLogout = () => {
        dispatch(logout());
        navigate(ROUTES.HOME);
    };

    return (
        <div className="mt-28 bg-white">
            <div className="bg-gray-50">
                <img
                    alt=""
                    src="https://images.pexels.com/photos/175934/pexels-photo-175934.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                    className="h-32 w-full object-cover lg:h-96"
                />

                <div className="mx-auto max-w-2xl px-4 lg:max-w-7xl lg:px-8">
                    <div className="sm:flex sm:items-end sm:space-x-5">
                        <div className="sm:flex sm:min-w-0 sm:flex-1 sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
                            <div className="mt-6 mb-6 min-w-0 flex-1 sm:hidden md:block">
                                <h1 className="truncate text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
                            </div>
                            <div className="mt-2 flex flex-col justify-stretch space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
                                <button
                                    type="button"
                                    className="hidden inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                    <EnvelopeIcon className="-ml-0.5 mr-1.5 size-5 text-gray-400"/>
                                    <span>{t('dashboard.messages')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="mx-auto max-w-2xl px-4 lg:max-w-7xl lg:px-8">
                <div className="pb-24">
                    <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                        <Tab.List className="border-b border-gray-200">
                            <div className="mt-4 -mb-px flex space-x-8">
                                <Tab className={({ selected }) =>
                                    classNames(
                                        selected
                                            ? 'border-sky-500 text-sky-600'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                        'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium'
                                    )
                                }>
                                    {t('dashboard.tabs.favorites')} ({favorites?.length || 0})
                                </Tab>
                                <Tab className={({ selected }) =>
                                    classNames(
                                        selected
                                            ? 'border-sky-500 text-sky-600'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                        'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium'
                                    )
                                }>
                                    {t('dashboard.tabs.savedSearches')} ({savedSearches?.length || 0})
                                </Tab>
                            </div>
                        </Tab.List>
                        <Tab.Panels className="mt-8">
                            <Tab.Panel>
                                <div className="divide-y divide-gray-100">
                                    {favorites?.map((favorite) => (
                                        <Link
                                            to={getRoute(ROUTES.ESTATE_DETAIL, { id: favorite.estate.id })}
                                            key={`favorite-${favorite.id}`}
                                            className="flex items-center gap-4 py-4 hover:bg-gray-50 px-4 -mx-4"
                                        >
                                            <div className="flex-shrink-0">
                                                <img
                                                    src={favorite.estate.thumb}
                                                    alt=""
                                                    className="h-24 w-32 rounded-lg object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900">
                                                    {favorite.estate.title}
                                                </p>
                                                <p className="mt-1 text-sm text-gray-500">
                                                    {favorite.estate.region?.name}
                                                </p>
                                                <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                                                    <span>{favorite.estate.bedrooms} {t('dashboard.property.rooms')}</span>
                                                    <span>{favorite.estate.living_area} m²</span>
                                                    <span>{favorite.estate.type}</span>
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0 text-right">
                                                <p className="font-medium text-gray-900">
                                                    {formatPriceSummary(favorite.estate.price)}
                                                </p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </Tab.Panel>
                            <Tab.Panel>
                                <div className="divide-y divide-gray-100">
                                    {savedSearches?.map((search) => (
                                        <Link
                                            to={search.search_url}
                                            key={`search-${search.id}`}
                                            className="flex items-center justify-between py-4 hover:bg-gray-50 px-4 -mx-4"
                                        >
                                            <div>
                                                <p className="font-medium text-gray-900">{search.name}</p>
                                                <p className="mt-1 text-sm text-gray-500">{t('dashboard.savedSearch.saved')} {new Date(search.created_at).toLocaleDateString()}</p>
                                            </div>
                                            <button className="rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500">
                                                {t('dashboard.savedSearch.goToSearch')}
                                            </button>
                                        </Link>
                                    ))}
                                </div>
                            </Tab.Panel>
                        </Tab.Panels>
                    </Tab.Group>
                </div>
            </main>
        </div>
    );
}